import Ionicons from "@expo/vector-icons/Ionicons";
import type { ComponentProps } from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import {
  FlatList,
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  View,
} from "react-native";

import { Modal } from "@/components/ui/app-modal";
import { useTranslation } from "react-i18next";

import { ThemedText } from "@/components/themed-text";
import { BlurredBackdrop } from "@/components/ui/blurred-backdrop";
import {
  ALL_EMOJI,
  EMOJI_GROUPS,
  searchEmoji,
  type EmojiEntry,
} from "@/constants/emoji";
import { Radius, Spacing } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import { socialStrings as strings } from "@/i18n/features/social.i18n";

type IoniconName = ComponentProps<typeof Ionicons>["name"];

const COLUMNS = 8;

/**
 * An in-app emoji picker, used on web where the OS one is out of reach.
 *
 * A browser CANNOT open the system emoji picker: ⌃⌘Space and Win+. are OS
 * shortcuts with no API behind them. The previous attempt — a small text field
 * the user was expected to type or paste into — put that limitation in front of
 * the person instead of solving it. Native does not use this at all; it raises
 * the real system keyboard, which is strictly better when it exists.
 *
 * The grid is a FlatList rather than a mapped ScrollView because the dataset is
 * hundreds of rows and this repo does not render growable lists eagerly.
 */
export function EmojiPicker({
  visible,
  onSelect,
  onClose,
}: {
  visible: boolean;
  onSelect: (emoji: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const c = useTheme();
  const styles = useMemo(() => createStyles(), []);
  const [query, setQuery] = useState("");
  const [groupIndex, setGroupIndex] = useState(0);
  const listRef = useRef<FlatList<EmojiEntry[]>>(null);

  const searching = query.trim().length > 0;
  const entries = useMemo(
    () => (searching ? searchEmoji(query) : EMOJI_GROUPS[groupIndex].entries),
    [searching, query, groupIndex],
  );

  // Chunked into rows so the FlatList virtualises ROWS. `numColumns` would work
  // too, but it re-keys the whole grid whenever the column count changes, and
  // this list swaps its data on every keystroke.
  const rows = useMemo(() => {
    const out: EmojiEntry[][] = [];
    for (let i = 0; i < entries.length; i += COLUMNS) {
      out.push(entries.slice(i, i + COLUMNS));
    }
    return out;
  }, [entries]);

  const pick = useCallback(
    (emoji: string) => {
      onSelect(emoji);
      setQuery("");
      onClose();
    },
    [onSelect, onClose],
  );

  const selectGroup = useCallback((index: number) => {
    setQuery("");
    setGroupIndex(index);
    listRef.current?.scrollToOffset({ offset: 0, animated: false });
  }, []);

  const close = useCallback(() => {
    setQuery("");
    onClose();
  }, [onClose]);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={close}
    >
      <View style={styles.backdrop}>
        <BlurredBackdrop onPress={close} />

        <View
          style={[
            styles.sheet,
            { backgroundColor: c.background, borderColor: c.border },
          ]}
        >
          <View style={styles.header}>
            <View
              style={[
                styles.searchBox,
                { backgroundColor: c.surface, borderColor: c.border },
              ]}
            >
              <Ionicons name="search" size={16} color={c.textMuted} />
              <TextInput
                value={query}
                onChangeText={setQuery}
                placeholder={t(strings.emojiSearch)}
                placeholderTextColor={c.textMuted}
                autoCapitalize="none"
                autoCorrect={false}
                style={[styles.searchInput, { color: c.text }]}
              />
            </View>

            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t(strings.emojiPickerClose)}
              onPress={close}
              hitSlop={8}
              style={[styles.close, { backgroundColor: c.backgroundElement }]}
            >
              <Ionicons name="close" size={18} color={c.textMuted} />
            </Pressable>
          </View>

          {rows.length === 0 ? (
            <View style={styles.empty}>
              <ThemedText variant="caption" tone="muted">
                {t(strings.emojiNone)}
              </ThemedText>
            </View>
          ) : (
            <FlatList
              ref={listRef}
              data={rows}
              keyExtractor={(row, index) => `${row[0]?.char ?? ""}-${index}`}
              keyboardShouldPersistTaps="handled"
              initialNumToRender={8}
              windowSize={7}
              removeClippedSubviews
              style={styles.grid}
              contentContainerStyle={styles.gridContent}
              renderItem={({ item: row }) => (
                <View style={styles.row}>
                  {row.map((entry) => (
                    <Pressable
                      key={entry.char}
                      accessibilityRole="button"
                      accessibilityLabel={entry.name}
                      onPress={() => pick(entry.char)}
                      style={({ pressed }) => [
                        styles.cell,
                        pressed && { backgroundColor: c.backgroundSelected },
                      ]}
                    >
                      <ThemedText style={styles.cellText}>
                        {entry.char}
                      </ThemedText>
                    </Pressable>
                  ))}
                  {/* Keeps the last row left-aligned rather than justified. */}
                  {row.length < COLUMNS
                    ? Array.from({ length: COLUMNS - row.length }).map(
                        (_, index) => (
                          <View key={`pad-${index}`} style={styles.cell} />
                        ),
                      )
                    : null}
                </View>
              )}
            />
          )}

          {searching ? null : (
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              // Nine fixed tabs — a plain ScrollView is correct here, unlike the
              // grid above.
              contentContainerStyle={styles.tabs}
              style={[styles.tabBar, { borderTopColor: c.border }]}
            >
              {EMOJI_GROUPS.map((group, index) => {
                const selected = index === groupIndex;
                return (
                  <Pressable
                    key={group.id}
                    accessibilityRole="button"
                    accessibilityState={{ selected }}
                    accessibilityLabel={t(group.labelKey)}
                    onPress={() => selectGroup(index)}
                    style={[
                      styles.tab,
                      selected && { backgroundColor: c.backgroundSelected },
                    ]}
                  >
                    <Ionicons
                      name={group.icon as IoniconName}
                      size={18}
                      color={selected ? c.tint : c.textMuted}
                    />
                  </Pressable>
                );
              })}
            </ScrollView>
          )}
        </View>
      </View>
    </Modal>
  );
}

/** Exported for tests: the picker must never offer what the API refuses. */
export const EMOJI_PICKER_ENTRY_COUNT = ALL_EMOJI.length;

function createStyles() {
  return StyleSheet.create({
    backdrop: {
      flex: 1,
      justifyContent: "flex-end",
      alignItems: "center",
    },
    sheet: {
      width: "100%",
      maxWidth: 420,
      maxHeight: 420,
      borderTopLeftRadius: Radius.lg,
      borderTopRightRadius: Radius.lg,
      borderWidth: StyleSheet.hairlineWidth,
      overflow: "hidden",
    },
    header: {
      flexDirection: "row",
      alignItems: "center",
      gap: Spacing.sm,
      paddingHorizontal: Spacing.md,
      paddingTop: Spacing.md,
      paddingBottom: Spacing.sm,
    },
    searchBox: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
      gap: Spacing.xs,
      paddingHorizontal: Spacing.sm,
      height: 36,
      borderRadius: Radius.pill,
      borderWidth: 1,
    },
    searchInput: {
      flex: 1,
      minWidth: 0,
      fontSize: 15,
      paddingVertical: 0,
    },
    close: {
      width: 32,
      height: 32,
      borderRadius: 16,
      alignItems: "center",
      justifyContent: "center",
    },
    grid: { flexGrow: 0 },
    gridContent: {
      paddingHorizontal: Spacing.sm,
      paddingBottom: Spacing.sm,
    },
    row: { flexDirection: "row" },
    cell: {
      flex: 1,
      aspectRatio: 1,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: Radius.sm,
    },
    cellText: { fontSize: 24, lineHeight: 30 },
    empty: {
      paddingVertical: Spacing.xl,
      alignItems: "center",
    },
    tabBar: { borderTopWidth: StyleSheet.hairlineWidth },
    tabs: {
      paddingHorizontal: Spacing.sm,
      paddingVertical: Spacing.xs,
      gap: Spacing.xs,
    },
    tab: {
      width: 36,
      height: 32,
      borderRadius: Radius.sm,
      alignItems: "center",
      justifyContent: "center",
    },
  });
}
